import type { AddressObject } from 'components/shared/AddressForm';
import { Dispatch, SetStateAction, ReactNode } from 'react';
import { AffiliateDetails } from 'utils/affiliateHelpers';
import { ProductPurchaseType } from 'content/products';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Currency } from 'utils/Currency';
import { Campaign, SddDeliveryBanner } from 'content/campaigns';

export enum ProductType {
  M1 = 'M1',
  RING = 'RING',
  RING_PRO = 'RING_PRO',
  RING_RARE = 'RING_RARE',
  RING_DIESEL = 'RING_DIESEL',
  MERCH = 'MERCH',
  GENERIC = 'GENERIC',
  PERFORMANCE_LAB = 'PERFORMANCE_LAB',
  UHX = 'UHX',
  MAX_SUBSCRIPTION = 'MAX_SUBSCRIPTION',
  SUPPLEMENT = 'SUPPLEMENT',
}

// Non-physical products that can't be shipped/gift-wrapped.
export const DIGITAL_PRODUCT_TYPES: ProductType[] = [
  ProductType.PERFORMANCE_LAB,
  ProductType.UHX,
  ProductType.MAX_SUBSCRIPTION,
];

export const isDigitalProductType = (
  type?: ProductType | string | null,
): boolean => DIGITAL_PRODUCT_TYPES.includes(type as ProductType);

export enum CartFlow {
  OG = 'OG',
  GIFT = 'GIFT',
}

export enum CartFlowScreens {
  'ITEM_LIST',
  'GIFT_NOTE',
}

export type CartHydrationState = 'pending' | 'ready' | 'failed';

export enum ProductUiTags {
  NO_EDIT = 'NO_EDIT',
  NO_QTY_BTNS = 'NO_QTY_BTNS',
  HIDE_IN_CART = 'HIDE_IN_CART',
}

export type Line = {
  merchandiseId: string;
  quantity: number;
  attributes: {
    key: string;
    value: string;
  }[];
}[];

export type Node = {
  id: string;
  merchandise: { product: { title: string } };
  cost: { totalAmount: { amount: number } };
};

export type addToCartArgsType = {
  pid?: ProductPurchaseType | string;
  shopifyVariantId?: string;
  productType: ProductType;
  discountCode?: string;
  /** Further codes this line carries into the cart's `discountCodes` (added
   * before `discountCode`). */
  discountCodes?: ReadonlyArray<string>;
  /** Shopify selling plan gid; makes the line a subscription at checkout. */
  sellingPlanId?: string;
  params?: {
    productType?: string;
    maxQuantity?: number;
    discountCode?: string;
    noteAttributes?: ReadonlyArray<Record<string, string>>;
    cartNoteAttributes?: ReadonlyArray<Record<string, string>>;
  };
};

export type CartContextType = {
  cart: Record<string, any> | null;
  cartHydrationState: CartHydrationState;

  store: ShopifyStore | null;
  currency: Currency | null;
  taxes: number;
  duty: number;
  setStore: Dispatch<SetStateAction<ShopifyStore | null>>;

  isCartVisible: boolean;
  showCart: () => void;
  hideCart: () => void;

  // Upsell modal state
  isUpsellVisible: boolean;
  showUpsellModal: () => void;
  hideUpsellModal: () => void;
  proceedWithUpsell: (
    upsellItems?: ReadonlyArray<addToCartArgsType>,
  ) => Promise<void>;
  addToCartSilently: (
    items: ReadonlyArray<addToCartArgsType>,
  ) => Promise<boolean>;

  createCartWithItem: (
    lines: Line,
    shouldShowCart?: boolean,
  ) => Promise<boolean>;
  // Resolves true when the lines were added (false: blocked, API error).
  addToCart: (
    argsArray: ReadonlyArray<addToCartArgsType>,
    showUpsell?: boolean,
  ) => Promise<boolean>;

  updateCartQuantity: (
    clid: string,
    currentQuantity: number,
    attributes?: Array<Record<string, string>>,
  ) => Promise<void>;

  incrementCartQuantity: (cartLine: any) => Promise<void>;

  addToCartByShopifyVariantId: (vid: ProductPurchaseType) => Promise<void>;

  removeFromCart: (
    clid: string,
    currentQuantity: number,
    attributes?: Array<Record<string, string>>,
  ) => Promise<void>;

  deleteFromCart: (clid: string) => Promise<void>;

  checkout: (shippingAddress?: AddressObject & { email?: string }) => void;
  updateCartAttributes: (
    cartId: string,
    params: Array<Record<string, string>>,
  ) => any;
  altContent: ReactNode;

  setAltContent: Dispatch<SetStateAction<React.ReactNode | null>>;
  referralDiscount: number;

  ultrahumanXAdded: Node | null;

  deleteCartFromLocalStorage: () => void;
  affiliateDetails: AffiliateDetails | null;
  campaignActive: boolean;

  // Campaign data
  campaigns: Campaign[];
  sddDelivery: SddDeliveryBanner | null;
  /** Quiet campaigns refresh (no `campaignsLoading`). Used on IST window crossing. */
  refetchCampaigns: () => void;
  campaignsLoading: boolean;
  campaignsError: Error | null;
  getCampaignForProduct: (
    product: 'ring' | 'ring-pro' | 'home' | 'cgm' | 'gift-card',
  ) => Campaign | undefined;

  cartFlow: CartFlow;
  setCartFlow: (input: CartFlow) => void;

  cartLoading: boolean;

  activeFlowScreen: CartFlowScreens;
  setActiveFlowScreen: (input: CartFlowScreens) => void;

  getCartAttributeValue: (input: string) => any;
  getCartAttributeIndex: (input: string) => number | undefined;

  error: string | null;
  setError: (input: string | null) => void;
  applicableDiscountCoupons: Array<string>;
  // Attributes + discount codes the Apple Pay express cart must carry so it
  // prices identically to the regular cart.
  getExpressCartInput: () => {
    attributes: Array<Record<string, string>>;
    discountCodes: Array<string>;
  };
  referral?: string;
  taxString: string;
  hasOnlyNonStandaloneItems: boolean;
  cartType: ProductType | undefined;
  workAffiliateDiscount?: workAffiliateDiscountType;
};

export type workAffiliateDiscountType = {
  affiliateCode: string;
  discount: number;
  discountCode: string;
};
